import React from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import { Form, Field } from "react-final-form";
import createDecorator from "final-form-calculate";
import SelectPack from "components/SelectPack2";
import Nutrients, { validateNutrients, nutrientDecorators } from "components/Nutrients";
import InputPack from "components/InputPack";
import Alert from "components/Alert";
import * as FormUtils from "common/FormUtils";
import * as fu from "./_utils";
import * as utils from "common/utils";
import { modalInlineClose } from "containers/App/_actions";
import * as validations from "common/validations";
import CheckboxPack from "components/CheckboxPack";
import ActionLink from "components/ActionLink";
import BlockSelector from "components/new/BlockSelector";
import Grid from "components/Grid";
import GridCell from "components/GridCell";

const getBlockIds = (values, isCrop) => {
    return isCrop ? (values.blockIds ? [values.blockIds] : []) : values.blockIds;
};

class Activity extends React.Component {
    constructor(props) {
        super(props);
        const viewModel = this.getViewModel();
        this.state = { viewModel };
        this.decorators = [
            nutrientDecorators("application"),
            createDecorator({
                field: "application.limeDissolvesWithinYear",
                updates: (fieldValue, fieldName, allValues) => {
                    const vm = Object.assign({}, allValues);
                    if (fieldValue) {
                        delete vm.application.limeYearsSinceApplied;
                    }
                    return vm;
                },
            }),
        ];
    }

    getViewModel() {
        let { blockIds = [] } = this.props.application;

        const previousLimeBlocks = ["ProductivePasture", "ProductiveFruit", "ProductiveCrop"];
        let previousLimeUsedBlockIds = this.props.budget.fertiliser
            .filter((f) => f.isLastPreviousLime && f.applications && f.id !== this.props.fertiliser.id)
            .reduce((a, b) => [...a, ...b.applications], [])
            .reduce((a, b) => [...a, ...b.blockIds], []);
        const { applications = [] } = this.props.fertiliser;
        applications.forEach((a) => {
            const { blockIds: appBlocks = [] } = a;
            previousLimeUsedBlockIds = [...previousLimeUsedBlockIds, ...appBlocks];
        });

        const applicationBlocks = this.props.fertiliser.isLastPreviousLime ? this.props.budget.blocks.filter((b) => previousLimeBlocks.includes(b.type) && (!previousLimeUsedBlockIds.includes(b.id) || blockIds.includes(b.id))) : this.props.budget.blocks.filter((b) => b.isProductive === true);

        const availableBlocks = this.props.isCrop ? applicationBlocks.filter((b) => ["FodderCrop", "ProductiveCrop"].includes(b.type)).map((block) => ({ ...block, ...{ value: block.id, text: block.name } })) : applicationBlocks.filter((b) => !["FodderCrop", "ProductiveCrop", "ProductiveOutdoorPigs"].includes(b.type)).map((block) => ({ ...block, ...{ value: block.id, text: block.name } }));

        const selectedBlocks = this.props.budget.blocks.filter((b) => blockIds.includes(b.id)).map((b) => ({ ...b, ...{ value: b.id, text: b.name } }));

        return {
            application: this.props.application,
            availableBlocks,
            selectedBlocks,
            isCrop: this.props.isCrop,
            blockIds: this.props.isCrop ? (blockIds.length > 0 ? blockIds[0] : "") : blockIds,
        };
    }

    closeModal = () => {
        this.props.modalInlineClose();
    };

    _closeOnEscape = (e) => {
        if (e.keyCode === 27) {
            this.closeModal();
        }
    };

    componentDidMount() {
        document.addEventListener("keydown", this._closeOnEscape);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this._closeOnEscape);
    }

    componentDidUpdate() {
        if (this.state.correlationId === this.props.correlationId) return;
        const viewModel = this.getViewModel();
        this.setState({ viewModel, correlationId: this.props.correlationId });
    }

    validate = (nutrientsOnApplication) => (values) => {
        const errors = { application: {} };
        const { fertiliser = {} } = this.props;
        const selectedBlockIds = getBlockIds(values, this.props.isCrop);
        const selectedBlocks = selectedBlockIds.map((id) => {
            return this.props.budget.blocks.find((b) => b.id === id);
        });
        const showYearsSinceApplied = fertiliser.material === fu.ProductType.Lime && values.application.limeDissolvesWithinYear !== true && fertiliser.isLastPreviousLime;
        const nutrients = (nutrientsOnApplication ? values.application.nutrients : fertiliser.nutrients) || {};
        const hasCrops = selectedBlocks.some((b) => ["FodderCrop", "ProductiveCrop"].includes(b.type));
        const showNFertAppMethod = !fu.hideNFertAppMethod(nutrients, hasCrops, fertiliser);
        if (showNFertAppMethod) {
            errors.application.nFertAppMethod = validations.required(values.application.nFertAppMethod) || values.application.nFertAppMethod === "None" ? "Required" : undefined;
        }
        if (showYearsSinceApplied) {
            errors.application.limeYearsSinceApplied = validations.required(values.application.limeYearsSinceApplied);
            errors.application.limeYearsSinceApplied = errors.application.limeYearsSinceApplied || validations.range(1, 5)(values.application.limeYearsSinceApplied);
        }
        errors.blockIds = validations.required(values.blockIds);
        if (nutrientsOnApplication) {
            const nutrientErrors = validateNutrients(values.application, "Soluble");
            errors.application = { ...errors.application, ...nutrientErrors };
        } else {
            errors.application.amount = validations.required(values.application.amount);
            errors.application.amount = errors.application.amount || validations.range(1, 10000000)(values.application.amount);
        }

        return errors;
    };

    onSubmit = (values) => {
        const application = values.application;
        application.blockIds = getBlockIds(values, this.props.isCrop);
        this.props.saveApplication(application);
        this.closeModal();
    };

    render() {
        const fertiliserUnits = this.props.fertiliser.isLastPreviousLime
            ? [
                  { text: "tonnes", value: "tonnes" },
                  { text: "kgs ", value: "kg" },
                  { text: "kgs/ha", value: "kgha" },
              ]
            : [
                  { text: "tonnes/month", value: "tonnes" },
                  { text: "kgs/month ", value: "kg" },
                  { text: "kgs/ha/month", value: "kgha" },
              ];

        const fertiliserUnitsForDairy = [{ text: "litres/ha/month", value: "kgha" }];

        const { nitrogenFertAppMethod = [] } = this.props.refData;
        const kghaOnly = fu.kghaOnly(this.props.viewModel);
        const units = kghaOnly ? fertiliserUnitsForDairy : fertiliserUnits;
        let amountLabel = this.props.fertiliser.isLastPreviousLime ? "Kg/ha" : "Kg/ha/month";
        let nutrientsOnApplication = fu.nutrientsOnApplication(this.props.fertiliser);

        return (
            <Form onSubmit={this.onSubmit} initialValues={this.state.viewModel} decorators={this.decorators} validate={this.validate(nutrientsOnApplication)}>
                {({ form, values, handleSubmit, errors }) => {
                    const { fertiliser = {} } = this.props;
                    const selectedBlockIds = getBlockIds(values, this.props.isCrop);
                    const selectedBlocks = selectedBlockIds.map((id) => {
                        return this.props.budget.blocks.find((b) => b.id === id);
                    });
                    const hasFruit = selectedBlocks.some((b) => b.type === "ProductiveFruit");
                    const showLimeDissolves = fertiliser.material === fu.ProductType.Lime;
                    const showYearsSinceApplied = showLimeDissolves && values.application.limeDissolvesWithinYear !== true && fertiliser.isLastPreviousLime;
                    let amount = 0;
                    let kgAmount = 0;
                    const kgHaMth = () => {
                        const tonnesHaMth = fu.calcTonnesPerHaMonth(values.application.amount, values.application.unit, this.props.budget.blocks, selectedBlockIds);
                        return utils.round((selectedBlockIds.length === 0 ? 0 : tonnesHaMth) * 1000, 2);
                    };
                    switch (values.application.unit) {
                        case "kgha":
                            amount = fu.calcTotalKgPerMonth(values.application.amount, fu.getApplicationBlockArea(selectedBlockIds, this.props.budget.blocks));
                            amountLabel = "Total Kgs";
                            kgAmount = values.application.amount || 0;
                            break;
                        case "kg":
                        case "tonnes":
                            amount = kgHaMth();
                            amountLabel = "KG/ha";
                            kgAmount = amount;
                            break;
                        default:
                            amount = 0;
                            break;
                    }
                    const nv = (nutrient) => {
                        const adjNut = (!nutrient || nutrient === null ? 0 : nutrient) / 100;
                        return utils.round(adjNut * kgAmount, 0);
                    };
                    const nutrients = (nutrientsOnApplication ? values.application.nutrients : fertiliser.nutrients) || {};
                    const organicCompost = fertiliser.material === fu.ProductType.Organic && fertiliser.organicType === "CompostMulches";
                    const organicOther = fertiliser.material === fu.ProductType.Organic && fertiliser.organicType === "Otherorganicmaterial";
                    const npks = (fertiliser.material !== "Organic" || organicCompost || organicOther) && (nutrients.N || nutrients.P || nutrients.K || nutrients.S) ? `${nv(nutrients.N)}-${nv(nutrients.P)}-${nv(nutrients.K)}-${nv(nutrients.S)}` : null;
                    const hasCrops = selectedBlocks.some((b) => ["FodderCrop", "ProductiveCrop"].includes(b.type));
                    const showNFertAppMethod = !fu.hideNFertAppMethod(nutrients, hasCrops, fertiliser);

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="Modal_wrapper">
                                <input type="hidden" id="fertiliser_activity_id" value={values.application.id} />
                                <div className="Modal Modal--medium">
                                    <div className="Modal-head">
                                        <span className="Modal-head--left">Fertiliser application</span>
                                        <span className="Modal-head--right">
                                            <ActionLink onClick={this.props.toggleQuickTips} className="Modal-close">
                                                <i className="icon icon-question" title={`${this.props.showQuickTips ? "Hide" : "Show"} quick tips`} />
                                            </ActionLink>
                                            <ActionLink onClick={this.closeModal} id="fertiliser_modal_close" className="Modal-close">
                                                <i className="icon icon-cross" title="Close" />
                                            </ActionLink>
                                        </span>
                                    </div>
                                    <div className="Modal-body">
                                        <Grid>
                                            <GridCell>
                                                <Field name="blockIds" isMulti={!this.props.isCrop} component={BlockSelector} availableBlocks={values.availableBlocks} />
                                            </GridCell>
                                            {showNFertAppMethod && (
                                                <GridCell className="u-width1of2">
                                                    <Field name="application.nFertAppMethod" id="application.nFertAppMethod" label="Method" component={SelectPack} required={true} options={nitrogenFertAppMethod} placeholder="Select application method" />
                                                </GridCell>
                                            )}
                                        </Grid>
                                        {nutrientsOnApplication && <Nutrients type="Soluble" errors={errors} refData={this.props.refData} nutrients={values.application.nutrients} solubleUnits={units} prefix="application.nutrients" />}
                                        {!nutrientsOnApplication && (
                                            <Grid>
                                                <GridCell className="u-width1of4">
                                                    <Field name="application.amount" id="application.amount" label="Amount" component={InputPack} requiredLabel={true} format={FormUtils.formatters.formatDecimal(0)} formatOnBlur />
                                                </GridCell>
                                                <GridCell className="u-width1of4">
                                                    <Field name="application.unit" id="application.unit" label="Unit" component={SelectPack} required={true} options={units} placeholder="Please select" disabled={organicCompost || kghaOnly} />
                                                </GridCell>
                                                <GridCell className="u-width1of4">
                                                    <InputPack meta={{ nrf: true }} label={amountLabel} id="application.tonnes" placeholder={amountLabel} disabled={true} value={(amount || 0).toLocaleString()} />
                                                </GridCell>
                                                {npks && (
                                                    <GridCell className="u-width1of4">
                                                        <InputPack meta={{ nrf: true }} label="NPKS (kg nutrient/ha)" id="application.npks" placeholder="NPKS (kg nutrient/ha)" disabled={true} value={npks} />
                                                    </GridCell>
                                                )}
                                            </Grid>
                                        )}
                                        <Grid>
                                            {hasFruit && !fertiliser.isLastPreviousLime && (
                                                <GridCell>
                                                    <Field name="application.fertiliserNPlacedUnderDripLine" id="application.fertiliserNPlacedUnderDripLine" label="Under drip line" type="checkbox" component={CheckboxPack} />
                                                </GridCell>
                                            )}
                                            {showLimeDissolves && (
                                                <GridCell>
                                                    <Field name="application.limeDissolvesWithinYear" id="application.limeDissolvesWithinYear" label="Dissolves within one year" type="checkbox" component={CheckboxPack} tip="For rates >0.5 T/ha (500 kg/ha) the model assumes that the lime dissolves over more than 1 year. If a finely ground lime is applied that will dissolve in the first year, select the box Dissolves within 1 year." />
                                                </GridCell>
                                            )}
                                            {showYearsSinceApplied && (
                                                <GridCell className="u-width1of4">
                                                    <Field name="application.limeYearsSinceApplied" id="application.limeYearsSinceApplied" label="Years since applied" component={InputPack} placeholder="Amount" required format={FormUtils.formatters.formatDecimal(0)} formatOnBlur />
                                                </GridCell>
                                            )}
                                            <GridCell>
                                                <Alert className="u-mt-md" type="info" text="Where pasture blocks have fodder crop blocks rotating through them, the application applies to the area of pasture only." />
                                            </GridCell>
                                        </Grid>
                                        <div className="Modal-footer">
                                            <div className="ButtonBar">
                                                <div className="ButtonBar-left">
                                                    <button type="button" className="Button Button--secondary" id="application-cancel" onClick={this.closeModal}>
                                                        Cancel
                                                    </button>
                                                </div>
                                                <div className="ButtonBar-right">
                                                    <button type="submit" className="Button" id="application-save">
                                                        Done
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Form>
        );
    }
}

export default compose(withAppContext, withActions({ modalInlineClose }))(Activity);
